import React, { useCallback, useState, useEffect, FormEvent } from 'react';
import '../assets/Settings.css';
import { useAuth } from '../context/AuthContext';

export interface SettingsProps { }

export const Settings: React.FC<SettingsProps> = () => {
    const { logout } = useAuth();
    const [adminEmail, setAdminEmail] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    // Password change state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [pwSaving, setPwSaving] = useState(false);
    const [pwMessage, setPwMessage] = useState('');

    const fetchAdminEmail = useCallback(async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/settings/admin_email`, { credentials: 'include' as RequestCredentials });
            const data = await response.json();

            if (data.success && data.data) {
                setAdminEmail(data.data.value);
            }
        } catch (error) {
            console.error('Error fetching admin email:', error);
            setMessage('Failed to load settings');
        } finally {
            setLoading(false);
        }
    }, [BACKEND_URL]);

    useEffect(() => {
        fetchAdminEmail();
    }, [fetchAdminEmail]);

    const handleSave = async () => {
        if (!adminEmail || !adminEmail.includes('@')) {
            setMessage('Please enter a valid email address');
            return;
        }

        setSaving(true);
        setMessage('');

        try {
            const response = await fetch(`${BACKEND_URL}/api/settings/admin_email`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ value: adminEmail }),
            });

            const data = await response.json();

            if (data.success) {
                setMessage('Email updated successfully');
                setTimeout(() => setMessage(''), 3000);
            } else {
                setMessage('Failed to update admin email');
            }
        } catch (error) {
            console.error('Error saving admin email:', error);
            setMessage('Error saving settings');
        } finally {
            setSaving(false);
        }
    };

    const handleChangePassword = async (e: FormEvent) => {
        e.preventDefault();
        setPwMessage('');

        if (!currentPassword || !newPassword) {
            setPwMessage('Please fill in all fields');
            return;
        }
        if (newPassword.length < 6) {
            setPwMessage('New password must be at least 6 characters');
            return;
        }
        if (newPassword !== confirmPassword) {
            setPwMessage('New passwords do not match');
            return;
        }

        setPwSaving(true);
        try {
            const response = await fetch(`${BACKEND_URL}/api/admin/change-password`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    current_password: currentPassword,
                    new_password: newPassword,
                }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setPwMessage('Password updated successfully');
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                setTimeout(() => setPwMessage(''), 3000);
            } else {
                setPwMessage(data.detail || data.message || 'Failed to update password');
            }
        } catch (error) {
            console.error('Error changing password:', error);
            setPwMessage('Error changing password');
        } finally {
            setPwSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="page settings-container">
                <div className="settings-loading">
                    <div className="settings-loading-spinner" />
                    Loading settings...
                </div>
            </div>
        );
    }

    return (
        <div className="page settings-container">
            <div className="page__head">
                <div className="page__titles">
                    <h1 className="page__title">Settings</h1>
                    <p className="page__sub">Manage your admin account</p>
                </div>
            </div>

            <div className="settings-content">
                {/* Email notifications */}
                <section className="settings-section">
                    <div className="settings-header">
                        <span className="section-icon section-icon-email">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="4" width="20" height="16" rx="2" />
                                <polyline points="22,6 12,13 2,6" />
                            </svg>
                        </span>
                        <div>
                            <h2>Email notifications</h2>
                            <p className="section-description">Where order alerts are sent</p>
                        </div>
                    </div>

                    <div className="settings-content">
                        <div className="settings-form-group">
                            <label htmlFor="admin-email">Admin email</label>
                            <input
                                id="admin-email"
                                type="email"
                                value={adminEmail}
                                onChange={(e) => setAdminEmail(e.target.value)}
                                placeholder="you@example.com"
                                className="settings-input"
                            />
                            <span className="settings-help-text">
                                New orders, status updates and system alerts go here.
                                Check spam if they stop arriving.
                            </span>
                        </div>

                        {message && (
                            <div className={`settings-message ${message.includes('successfully') ? 'settings-message-success' : 'settings-message-error'}`}>
                                {message}
                            </div>
                        )}

                        <button
                            className="settings-btn settings-btn-primary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving…' : 'Save email'}
                        </button>
                    </div>
                </section>

                {/* Change password */}
                <section className="settings-section">
                    <div className="settings-header">
                        <span className="section-icon section-icon-password">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                        </span>
                        <div>
                            <h2>Password</h2>
                            <p className="section-description">Update your admin login</p>
                        </div>
                    </div>

                    <form className="settings-content" onSubmit={handleChangePassword} autoComplete="off">
                        <div className="settings-form-group">
                            <label htmlFor="current-password">Current password</label>
                            <input
                                id="current-password"
                                type="password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                className="settings-input"
                                autoComplete="current-password"
                            />
                        </div>

                        <div className="settings-row">
                            <div className="settings-form-group">
                                <label htmlFor="new-password">New password</label>
                                <input
                                    id="new-password"
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Min 6 characters"
                                    className="settings-input"
                                    autoComplete="new-password"
                                />
                            </div>
                            <div className="settings-form-group">
                                <label htmlFor="confirm-password">Confirm password</label>
                                <input
                                    id="confirm-password"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="settings-input"
                                    autoComplete="new-password"
                                />
                            </div>
                        </div>

                        {pwMessage && (
                            <div className={`settings-message ${pwMessage.includes('successfully') ? 'settings-message-success' : 'settings-message-error'}`}>
                                {pwMessage}
                            </div>
                        )}

                        <button
                            type="submit"
                            className="settings-btn settings-btn-primary"
                            disabled={pwSaving}
                        >
                            {pwSaving ? 'Updating…' : 'Update password'}
                        </button>
                    </form>
                </section>

                {/* Account */}
                <section className="settings-section">
                    <div className="settings-header">
                        <span className="section-icon section-icon-logout">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                <polyline points="16 17 21 12 16 7" />
                                <line x1="21" y1="12" x2="9" y2="12" />
                            </svg>
                        </span>
                        <div>
                            <h2>Account</h2>
                            <p className="section-description">Sign out on this device</p>
                        </div>
                    </div>
                    <div className="settings-content">
                        <button className="settings-btn settings-btn-danger" onClick={logout}>
                            Sign out
                        </button>
                    </div>
                </section>
            </div>
        </div>
    );
};
